import { useEffect, useRef } from 'react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import UserContextWrapper from 'context/common/User/UserContextWrapper'
import { NextRouter } from 'next/router'
import { sessionStorageKeys } from 'constants/common'
import 'react-toastify/dist/ReactToastify.css'
// test

//Redux
import { Provider } from 'react-redux'
import { store, persistor } from 'state/store'
import { PersistGate } from 'redux-persist/integration/react'

// All the global css files can be imported here
import 'theme/legacy/main.scss'
import '../globals.css'
import 'theme/legacy/_swiper.scss'
import { useRouter } from 'next/router'
import logPageView from 'utils/logPageView'
import { ToastContainer } from 'react-toastify'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import AuthContextWrapper from 'context/AuthContext'
import 'theme/2023Redesign/_typography_variables.scss'
import Datadog from 'app/DataDog'
import ErrorFallback from 'common/ErrorFallback'
import useRudderStackInit from 'hooks/useRudderStackInit'
import useHeapInit from 'hooks/useHeapInit'
import Script from 'next/script'
import useToast from 'hooks/useToast'
import { QueryClient, QueryClientProvider } from 'react-query'
import RudderStackContext from 'context/common/RudderStackContext'
import { roboto } from 'component_library/fonts'
import BookingsLayout from 'app/Bookings/Layout'

const queryClient = new QueryClient()

// This default export is required in a new `pages/_app.js` file.
function MyApp({ Component, pageProps }: AppProps): JSX.Element {
  const router = useRouter()
  const { asPath } = router
  const prevPathRef = useRef<string>(null)

  // calling useToast hook because, the SuccessBanner & ErrorBanner comp styles were not imported for few SSR pages
  // This issue occurs in build mode (not in dev mode)
  useToast()

  const isCallRailSwapScriptActive =
    useGetTreatment(TreatmentFlags.USE_CALLRAIL_JS) === treatmentFlagValues.ON
  const { rudderStackLoaded } = useRudderStackInit() // Ruderstack initialization hook

  useHeapInit() // Heap initialization hook

  const isDatadogEnabled =
    useGetTreatment(TreatmentFlags.DATADOG_FRONTEND_TRACKING) ===
    treatmentFlagValues.ENABLE
  const isErrorTrackingEnabled =
    isDatadogEnabled &&
    !!process.env.NEXT_PUBLIC_DATADOG_APP_ID &&
    !!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN

  useEffect(() => {
    storePreviousRouterQuery(router)
    const basePath = asPath.split('?')[0]

    if (basePath !== prevPathRef.current) {
      prevPathRef.current = basePath
      logPageView(basePath)
    }
  }, [asPath])

  // stores current router query object in browser's session, inorder to get that value as previous router query object
  const storePreviousRouterQuery = (router: NextRouter): void => {
    const { pathname, query } = router
    const routerObj = { query, pathname }

    // Set the previous query obj as the value of the current query obj.
    const prevRouterObj = sessionStorage.getItem(
      sessionStorageKeys.CURRENT_ROUTER
    )
    sessionStorage.setItem(sessionStorageKeys.PREVIOUS_ROUTER, prevRouterObj)

    // Set the current query obj value by looking at the browser's router object.
    sessionStorage.setItem(
      sessionStorageKeys.CURRENT_ROUTER,
      JSON.stringify(routerObj)
    )
  }

  return (
    <>
      <Head>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/images/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/images/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/images/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/images/favicon/safari-pinned-tab.svg"
          color="#eaaf19"
        />
        <meta name="msapplication-TileColor" content="#2b5797" />
        <meta name="theme-color" content="#e7e5cb" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=2"
        />

        {process.env.NEXT_PUBLIC_HEAP_APP_ID && (
          // This is not using `async` on purpose.
          // It needs to block while loading so that it's ready before the rest of the app tries to use it.
          <script type="text/javascript" src="/static/heapInit.js" />
        )}
      </Head>

      <Datadog isDatadogEnabled={isDatadogEnabled}>
        <ErrorFallback shouldLogError={isErrorTrackingEnabled}>
          <Provider store={store}>
            {/* BookingsLayout handles session and redux cleanup for skip email verification flow.
            Needs to be wrapped around the entire app to handle route changes to and from booking flow
            */}
            <BookingsLayout>
              <PersistGate persistor={persistor}>
                <AuthContextWrapper>
                  {/* Passed dispatch here because `UserContextWrapper` is used in other parts of the code 
                  without being wrapped in a `Provider` component. */}
                  <UserContextWrapper dispatch={store.dispatch}>
                    <QueryClientProvider client={queryClient}>
                      <RudderStackContext.Provider
                        value={{ rudderStackLoaded }}
                      >
                        <main
                          style={{ width: '100%', height: '100%' }}
                          className={`${roboto.className}`}
                        >
                          <Component {...pageProps} />
                        </main>
                      </RudderStackContext.Provider>
                    </QueryClientProvider>
                  </UserContextWrapper>
                </AuthContextWrapper>
                <ToastContainer hideProgressBar />
              </PersistGate>
            </BookingsLayout>
          </Provider>
        </ErrorFallback>
      </Datadog>

      {/* Script component allows the script tag to be added at the last of the body tag */}
      {isCallRailSwapScriptActive && (
        <Script
          async
          type="text/javascript"
          src="https://cdn.callrail.com/companies/786290431/6557eac645ff78314816/12/swap.js"
        />
      )}
    </>
  )
}

export default MyApp
